import { Container } from 'typescript-ioc';
import { isSameDay } from 'date-fns';
import { DateFnsFormats, IDateTimeFormatter, TimeFormats } from '../../infrastructure/date-formatter';
import { utcToZonedTime } from '../utils/date-fns-overrides.utils';
import { DateTimeFormatter } from '../implementations/date-formatter';

export const getFormattedDatesString = (
        startDateTime: Date,
        endDateTime: Date,
        timeZone?: string,
        shouldReturnOneDateIfSameDay = false
    ): string => {
    const dateFormatter = Container.get(IDateTimeFormatter);

    const formattedStartDate = dateFormatter.dateToFormattedString(
        startDateTime,
        DateFnsFormats.StringifiedDayMonthNumericDateYear,
        timeZone);

    const formattedEndDate = dateFormatter.dateToFormattedString(
        endDateTime,
        DateFnsFormats.StringifiedDayMonthNumericDateYear,
        timeZone);

    if (shouldReturnOneDateIfSameDay) {
        let startDateTimeZoned = startDateTime;
        let endDateTimeZoned = endDateTime;

        if (timeZone) {
            startDateTimeZoned = utcToZonedTime(startDateTime, timeZone);
            endDateTimeZoned = utcToZonedTime(endDateTime, timeZone);
        }

        if (isSameDay(startDateTimeZoned, endDateTimeZoned)) {
            return formattedStartDate;
        }
    }

    return `${formattedStartDate} - ${formattedEndDate}`;
};

export const getFormattedStartDateString = (startDateTime: Date, endDateTime?: Date, isUpperCased = true): string => {
    const dateFormatter = Container.get(IDateTimeFormatter);
    const is24Hours = dateFormatter.getLocalTimeFormat() === TimeFormats.Day24Hours;
    const format = is24Hours ?
        DateFnsFormats.StringifiedShortDayMonthNumericDateYear24Hours :
        DateFnsFormats.StringifiedShortDayMonthNumericDateYear12Hours;

    const formattedStartDate = dateFormatter.dateToFormattedString(startDateTime, format);
    const resultStartDate = isUpperCased ? formattedStartDate.toUpperCase() : formattedStartDate;

    if (!endDateTime || isSameDay(startDateTime, endDateTime)) {
        return resultStartDate;
    }

    const formattedEndDate = dateFormatter.dateToFormattedString(endDateTime, format);
    const resultEndDate = isUpperCased ? formattedEndDate.toUpperCase() : formattedEndDate;

    return `${resultStartDate} - ${resultEndDate}`;
};

export const getHourStringInEventTimezone = (dateTimeFormatter: DateTimeFormatter, date: Date, eventTimezone: string) => {
    const timeString = dateTimeFormatter.dateToFormattedString(
        date,
        TimeFormats.Day12Hours,
        eventTimezone
    );
    const [hour, rest] = timeString.split(':');
    const [, amOrPm] = rest.split(' ');
    return `${hour}:00${amOrPm}`.replace('AM', 'am')
        .replace('PM', 'pm');
};

export const getReportDateTimeString = (dateTime: Date, timezone?: string): string => {
    const dateFormatter = Container.get(IDateTimeFormatter);
    const is24Hours = dateFormatter.getLocalTimeFormat() === TimeFormats.Day24Hours;
    const format = is24Hours ?
        `${DateFnsFormats.ShortDayMonthYear24Hours} ${TimeFormats.Day24Hours}` :
        `${DateFnsFormats.ShortDayMonthYear12Hours} ${TimeFormats.Day12Hours}`;

    return dateFormatter.dateToFormattedString(dateTime, format, timezone) ;
};

export const getScheduledEmailDateString = (dateTime: Date, timezone: string): string => {
    const dateFormatter = Container.get(IDateTimeFormatter);
    const is24Hours = dateFormatter.getLocalTimeFormat() === TimeFormats.Day24Hours;
    const format = is24Hours ?
        DateFnsFormats.StringifiedShortDayMonthNumericDateYear24Hours :
        DateFnsFormats.StringifiedShortDayMonthNumericDateYear12Hours;

    return dateFormatter.dateToFormattedString(dateTime, format, timezone) ;
};

export const getScheduledEmailTimeString = (dateTime: Date, timezone: string): string => {
    const dateFormatter = Container.get(IDateTimeFormatter);
    const is24Hours = dateFormatter.getLocalTimeFormat() === TimeFormats.Day24Hours;
    const format = is24Hours ? TimeFormats.Day24Hours : TimeFormats.Day12Hours;

    return dateFormatter.dateToFormattedString(dateTime, format, timezone) ;
};

export const getUserTimeZoneOffsetString = (): string => {
    const offset = new Date().getTimezoneOffset();
    const o = Math.abs(offset);
    return (offset < 0 ? '+' : '-') + ('00' + Math.floor(o / 60)).slice(-2) + ':' + ('00' + (o % 60)).slice(-2);
};

export const getSuffixDay = (day: number): string => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
    }
};

export const getAnonimizationDateTimeString = (dateTime: Date): string => {
    const dateFormatter = Container.get(IDateTimeFormatter);
    const format = `${DateFnsFormats.StringifiedDayMonthNumericDateYear24Hours} ${TimeFormats.Day24Hours}`;
    return `${dateFormatter.dateToFormattedString(dateTime, format)} UTC`;
};

/* example: Wednesday 13th June 20:00 UTC */
export const formatUTCForAnonymizationDate = (anonymizationDate: Date): string => {
    let anonymizationDateString = '';
    const date = new Date(anonymizationDate);
    const result = new Date(date.toISOString().slice(0, -1));
    anonymizationDateString = getAnonimizationDateTimeString(result);
    const splittedDate = anonymizationDateString.split(' ');
    const suffixDay = getSuffixDay(Number(splittedDate[1]));
    splittedDate[1] = splittedDate[1] + suffixDay;
    const fullDate = splittedDate.join(' ');
    anonymizationDateString = fullDate;
    return anonymizationDateString;
};

export const getFullDateTimeStringInEventTimezone = (
    dateTimeFormatter: DateTimeFormatter,
    date: Date,
    eventTimezone: string,
    locale: TimeFormats
) => {
    const timeInEventTimeZone = dateTimeFormatter.dateToFormattedString(
        date,
        locale,
        eventTimezone
    );
    const dayInEventTimeZone = dateTimeFormatter.dateToLocalStringFormat(
        date,
        eventTimezone,
        locale
    );
    return `${dayInEventTimeZone} ${timeInEventTimeZone}`;
};

export const convertMinutesToTime = (value: string): string => {
    let numberValue = parseInt(value, 10);

    const hours = Math.floor(numberValue / 60);
    const minutes = numberValue % 60;

    const hoursString = hours < 10 ? '0' + hours : hours;
    const minutesString = minutes < 10 ? '0' + minutes : minutes;

    return `${hoursString}:${minutesString}:00`;
};

export const getMeetingArtifactDateTimeString = (dateFormatter: DateTimeFormatter, dateTime: Date, timezone?: string): string => {
    const is24Hours = dateFormatter.getLocalTimeFormat() === TimeFormats.Day24Hours;
    const format = is24Hours ?
        `${DateFnsFormats.StringifiedMonthDayYear} ${TimeFormats.Day24Hours}` :
        `${DateFnsFormats.StringifiedMonthDayYear} ${TimeFormats.Day12Hours}`;

    return dateFormatter.dateToFormattedString(dateTime, format, timezone) ;
};

export const getDateTimeStringForMixPanel = (dateTime: Date, eventTimezone: string): string => {
    const dateFormatter = Container.get(IDateTimeFormatter);
    const is24Hours = dateFormatter.getLocalTimeFormat() === TimeFormats.Day24Hours;
    const format = is24Hours ?
        `${DateFnsFormats.ShortDayMonthYear24Hours} ${TimeFormats.Day24Hours}` :
        `${DateFnsFormats.ShortDayMonthYear12Hours} ${TimeFormats.Day12Hours}`;

    return dateFormatter.dateToFormattedString(dateTime, format, eventTimezone) ;
};

export const getDateString = (dateTime: Date, dateFormat: string) => {
    const dateFormatter = Container.get(IDateTimeFormatter);
    return `${dateFormatter.dateToFormattedString(dateTime, dateFormat)}`;
};